/**
 * UserProfileModal.js
 *
 * 用户个人信息弹出窗口组件
 * 显示用户基本信息、文件统计、成本统计和最近工作流记录
 */
import React, { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, ProgressBar, Badge, Table, Spinner, Alert } from 'react-bootstrap';
import { FaUser, FaDollarSign, FaFolder, FaChartLine, FaTimes, FaBuilding } from 'react-icons/fa';
import { userAPI } from '../../utils/httpClient';
import { useI18n } from '../../i18n';

const UserProfileModal = ({ show, onHide }) => {
    const { t } = useI18n();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 获取用户信息
    const fetchUserProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await userAPI.getUserProfile();
            if (response.success) {
                setUserProfile(response.data);
            } else {
                setError(response.message || 'Failed to fetch user profile');
            }
        } catch (err) {
            console.error('Error fetching user profile:', err);
            setError('Network error: Failed to load user profile');
        } finally {
            setLoading(false);
        }
    };

    // 当弹窗显示时获取数据
    useEffect(() => {
        if (show) {
            fetchUserProfile();
        }
    }, [show]);

    // 格式化日期
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // 获取状态徽章样式
    const getStatusBadge = (status) => {
        switch (status) {
            case 'SUCCESS':
                return <Badge bg="success">✅ {status}</Badge>;
            case 'FAILED':
                return <Badge bg="danger">❌ {status}</Badge>;
            case 'PROCESSING':
            case 'STARTED':
                return <Badge bg="info">🔄 {status}</Badge>;
            case 'PENDING':
            case 'QUEUED':
                return <Badge bg="warning">⏳ {status}</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    // 获取成本进度条样式
    const getCostProgressVariant = (percentage) => {
        if (percentage >= 90) return 'danger';
        if (percentage >= 70) return 'warning';
        return 'success';
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaUser className="me-2" />
                    {t('userProfile:title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className="text-center p-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">{t('userProfile:loading')}</p>
                    </div>
                )}

                {error && (
                    <Alert variant="danger">
                        <strong>{t('userProfile:loadFailedPrefix')}</strong> {error}
                    </Alert>
                )}

                {userProfile && (
                    <div>
                        {/* 基本信息 */}
                        <Card className="mb-3">
                            <Card.Header>
                                <FaUser className="me-2" />
                                {t('userProfile:sections.basic')}
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6} xs={12}>
                                        <div className="p-2 border rounded bg-light">
                                            <div className="text-muted small mb-1">{t('userProfile:labels.username')}</div>
                                            <div className="h6 text-dark mb-0">
                                                <FaUser className="me-2 text-primary" />
                                                {userProfile.user_info.username}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <div className="p-2 border rounded bg-light">
                                            <div className="text-muted small mb-1">{t('userProfile:labels.organization')}</div>
                                            <div className="h6 text-dark mb-0">
                                                <FaBuilding className="me-2 text-info" />
                                                {userProfile.user_info.organization}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* 成本统计 */}
                        <Card className="mb-3">
                            <Card.Header>
                                <FaDollarSign className="me-2" />
                                {t('userProfile:sections.cost')}
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3 g-3">
                                    <Col md={6} xs={12}>
                                        <div className="p-2 border rounded bg-light">
                                            <div className="text-muted small mb-1">{t('userProfile:labels.currentCost')}</div>
                                            <div className="h5 text-success mb-0 fw-bold">
                                                ${userProfile.user_info.current_cost.toFixed(6)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <div className="p-2 border rounded bg-light">
                                            <div className="text-muted small mb-1">{t('userProfile:labels.costLimit')}</div>
                                            <div className="h5 text-warning mb-0 fw-bold">
                                                {userProfile.user_info.cost_limit > 0 ? `$${userProfile.user_info.cost_limit.toFixed(2)}` : t('userProfile:labels.unlimited')}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                {userProfile.user_info.cost_limit > 0 && (
                                    <div className="mt-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-semibold">{t('userProfile:labels.usage')}</span>
                                            <span className="badge bg-secondary">{userProfile.user_info.cost_usage_percentage.toFixed(1)}%</span>
                                        </div>
                                        <ProgressBar
                                            now={userProfile.user_info.cost_usage_percentage}
                                            variant={getCostProgressVariant(userProfile.user_info.cost_usage_percentage)}
                                            style={{ height: '12px' }}
                                            className="rounded"
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* 文件统计 */}
                        <Card className="mb-3">
                            <Card.Header>
                                <FaFolder className="me-2" />
                                {t('userProfile:sections.files')}
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={6} xs={12}>
                                        <div className="text-center p-3 border rounded shadow-sm bg-light">
                                            <div className="h6 mb-2 text-dark">{t('userProfile:labels.personalFiles')}</div>
                                            <div className="h4 text-primary mb-2 fw-bold">
                                                {userProfile.file_statistics.personal_files.total_size_formatted}
                                            </div>
                                            <div className="text-muted">
                                                <small>{userProfile.file_statistics.personal_files.file_count} {t('userProfile:labels.fileCountSuffix')}</small>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <div className="text-center p-3 border rounded shadow-sm bg-light">
                                            <div className="h6 mb-2 text-dark">{t('userProfile:labels.organizationFiles')}</div>
                                            <div className="h4 text-info mb-2 fw-bold">
                                                {userProfile.file_statistics.organization_files.total_size_formatted}
                                            </div>
                                            <div className="text-muted">
                                                <small>{userProfile.file_statistics.organization_files.file_count} {t('userProfile:labels.fileCountSuffix')}</small>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* 近期任务 */}
                        <Card>
                            <Card.Header>
                                <FaChartLine className="me-2" />
                                {t('userProfile:sections.recent')}
                            </Card.Header>
                            <Card.Body>
                                {userProfile.recent_workflows.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table hover size="sm" className="mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="text-center">{t('userProfile:labels.status')}</th>
                                                    <th className="text-center">{t('userProfile:labels.cost')}</th>
                                                    <th className="text-center d-none d-md-table-cell">{t('userProfile:labels.createdAt')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userProfile.recent_workflows.map((workflow, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center align-middle">
                                                            {getStatusBadge(workflow.status)}
                                                        </td>
                                                        <td className="text-center align-middle">
                                                            <span className="fw-semibold text-success">
                                                                ${workflow.completion_cost.toFixed(6)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center align-middle d-none d-md-table-cell">
                                                            <small className="text-muted">
                                                                {formatDate(workflow.created_at)}
                                                            </small>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>

                                        {/* Mobile-friendly time display */}
                                        <div className="d-md-none mt-2">
                                            {userProfile.recent_workflows.map((workflow, index) => (
                                                <div key={index} className="border-bottom py-1">
                                                    <small className="text-muted">
                                                        {t('userProfile:mobile.task')} {index + 1}: {formatDate(workflow.created_at)}
                                                    </small>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-4">
                                        <FaChartLine className="mb-2" size={24} style={{ opacity: 0.5 }} />
                                        <div>{t('userProfile:empty.noTasks')}</div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default UserProfileModal;
